import React, { useMemo } from "react";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import type { Components } from "react-markdown";
import { cn } from "utils";
import { ShellLink } from "@components/ShellNavigation/ShellAwareLink";
import Image from "next/image";
import ReactMarkdown from "react-markdown";

interface DocsMarkdownProps {
  /** Markdown 内容 */
  content: string;
  /** 自定义样式类名 */
  className?: string;
}

const MARKDOWN_SIGNAL_REGEX =
  /(^\s{0,3}(#{1,6})\s+.+$|^\s{0,3}>\s+.+$|^\s{0,3}(?:[-*+]\s+|\d+\.\s+).+$|```|!\[[^\]]*\]\([^\)]+\)|\[[^\]]+\]\([^\)]+\)|<https?:\/\/[^>]+>|(?:\*\*|__|\*|_|~~|`)|^\s*\|.+\|\s*$|^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?\s*$)/m;

const CJK_STRONG_FIX_REGEX = /([\u4e00-\u9fa5])(\*\*|__)(?=[“"‘'\[])/g;
const TABLE_SEPARATOR_REGEX =
  /^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?\s*$/;
const TABLE_ROW_REGEX = /^\s*\|.*\|\s*$/;

const hasMarkdownSyntax = (text: string): boolean =>
  MARKDOWN_SIGNAL_REGEX.test(text);

const looksLikePipeRow = (line: string): boolean => {
  const trimmed = line.trim();
  if (!trimmed || TABLE_SEPARATOR_REGEX.test(trimmed)) {
    return false;
  }

  return (trimmed.match(/\|/g) || []).length >= 2;
};

// Normalize imperfect AI-generated pipe tables so remark-gfm can build tbody reliably.
const normalizeMarkdownTables = (text: string): string => {
  const normalizedBreaks = text.replace(/\r\n?/g, "\n").replace(/\\n/g, "\n");
  const lines = normalizedBreaks.split("\n");
  const result: string[] = [];
  let lastNonEmpty = "";

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const trimmed = line.trim();
    const prevTrimmed = lastNonEmpty;
    const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : "";

    const isHeaderLine =
      TABLE_ROW_REGEX.test(trimmed) && TABLE_SEPARATOR_REGEX.test(nextLine);

    if (
      isHeaderLine &&
      result.length > 0 &&
      result[result.length - 1].trim() !== ""
    ) {
      result.push("");
    }

    const shouldNormalizeRow =
      looksLikePipeRow(trimmed) &&
      !TABLE_ROW_REGEX.test(trimmed) &&
      (TABLE_SEPARATOR_REGEX.test(prevTrimmed) ||
        TABLE_ROW_REGEX.test(prevTrimmed));

    if (shouldNormalizeRow) {
      result.push(`| ${trimmed.replace(/^\|+|\|+$/g, "").trim()} |`);
    } else {
      result.push(line);
    }

    if (trimmed !== "") {
      lastNonEmpty = trimmed;
    }
  }

  return result.join("\n");
};

const preprocessMarkdownContent = (raw: string): string => {
  const cjkFixed = raw.replace(CJK_STRONG_FIX_REGEX, "$1 $2");
  return normalizeMarkdownTables(cjkFixed);
};

/**
 * 专用于文档内容显示的 Markdown 组件
 * 使用项目主题变量和字体样式
 */
export const DocsMarkdown: React.FC<DocsMarkdownProps> = ({
  content,
  className,
}) => {
  // 修复 CJK 字符后紧跟 **“... 格式导致无法识别粗体的问题
  // 在 CJK 字符和 ** 之间添加空格，使其满足 CommonMark 的左侧强调定界符规则
  const processedContent = useMemo(() => {
    if (!content) {
      return "";
    }

    return preprocessMarkdownContent(content);
  }, [content]);

  // 自定义组件配置，专门针对文档内容优化
  const components: Partial<Components> = useMemo(() => {
    const baseComponents: Partial<Components> = {
      // 标题样式 - 使用主题字体类，添加 SEO 友好的 ID
      h1: ({ children, ...props }) => {
        const headingText = typeof children === "string" ? children : "";
        const headingId = headingText
          .toLowerCase()
          .replace(/[^a-z0-9\u4e00-\u9fff]+/g, "-")
          .replace(/^-|-$/g, "");

        return (
          <h1
            aria-label="Docs Markdown h1"
            data-openreplay-label="misc_docsmarkdown_h1"
            id={headingId}
            className="headline_regular_l text-content_primary mt-8 mb-4 first:mt-0"
            {...(props as any)}
          >
            {children}
          </h1>
        );
      },
      h2: ({ children, ...props }) => {
        const headingText = typeof children === "string" ? children : "";
        const headingId = headingText
          .toLowerCase()
          .replace(/[^a-z0-9\u4e00-\u9fff]+/g, "-")
          .replace(/^-|-$/g, "");

        return (
          <h2
            aria-label="Docs Markdown h2"
            data-openreplay-label="misc_docsmarkdown_h2"
            id={headingId}
            className="headline_regular_m text-content_primary mt-8 mb-6 first:mt-0"
            {...(props as any)}
          >
            {children}
          </h2>
        );
      },
      h3: ({ children, ...props }) => {
        const headingText = typeof children === "string" ? children : "";
        const headingId = headingText
          .toLowerCase()
          .replace(/[^a-z0-9\u4e00-\u9fff]+/g, "-")
          .replace(/^-|-$/g, "");

        return (
          <h3
            aria-label="Docs Markdown h3"
            data-openreplay-label="misc_docsmarkdown_h3"
            id={headingId}
            className="headline_regular_s text-content_primary mt-6 mb-3"
            {...(props as any)}
          >
            {children}
          </h3>
        );
      },
      h4: ({ children, ...props }) => (
        <h4
          aria-label="Docs Markdown h4"
          data-openreplay-label="misc_docsmarkdown_h4"
          id="sem-docs-markdown-h4"
          className="title_l text-content_primary mt-6 mb-3"
          {...(props as any)}
        >
          {children}
        </h4>
      ),
      h5: ({ children, ...props }) => (
        <h5
          aria-label="Docs Markdown h5"
          data-openreplay-label="misc_docsmarkdown_h5"
          id="sem-docs-markdown-h5"
          className="title_m text-content_primary mt-4 mb-2"
          {...(props as any)}
        >
          {children}
        </h5>
      ),
      h6: ({ children, ...props }) => (
        <h6
          aria-label="Docs Markdown h6"
          data-openreplay-label="misc_docsmarkdown_h6"
          id="sem-docs-markdown-h6"
          className="title_s text-content_primary mt-4 mb-2"
          {...(props as any)}
        >
          {children}
        </h6>
      ),

      // 段落样式
      p: ({ children, ...props }) => (
        <p
          className="body_output text-content_primary my-6 first:mt-0 last:mb-0"
          {...(props as any)}
        >
          {children}
        </p>
      ),

      // 强调文本 - 使用品牌色彩突出显示
      strong: ({ children, ...props }) => (
        <strong
          className="body_output text-content_primary font-semibold"
          {...(props as any)}
        >
          {children}
        </strong>
      ),

      // 斜体文本
      em: ({ children, ...props }) => (
        <em
          className="body_output text-content_tertiary italic"
          {...(props as any)}
        >
          {children}
        </em>
      ),

      // 链接样式
      a: ({ children, ...props }) => {
        const { href = "", ...restProps } = props;
        return (
          <ShellLink
            aria-label="Docs Markdown"
            data-openreplay-label="misc_docsmarkdown_open"
            id="lnk-docs-markdown"
            href={href}
            className="text-content_brand_primary hover:text-content_brand_primary underline transition-colors body_output"
            target="_blank"
            rel="noreferrer"
            {...(restProps as any)}
          >
            {children}
          </ShellLink>
        );
      },

      // 列表样式
      ul: ({ children, ...props }) => (
        <ul
          className="list-disc list-outside ml-6 my-4 space-y-2"
          {...(props as any)}
        >
          {children}
        </ul>
      ),

      ol: ({ children, ...props }) => (
        <ol
          className="list-decimal list-outside ml-6 my-4 space-y-2 [&>li::marker]:text-content_brand_primary [&>li::marker]:body_output"
          {...(props as any)}
        >
          {children}
        </ol>
      ),

      li: ({ children, ...props }) => (
        <li
          className="body_output text-content_primary leading-6"
          {...(props as any)}
        >
          {/* Re-parse plain string list items only; keep AST nodes untouched to avoid table fragmentation. */}
          {(() => {
            const childArray = React.Children.toArray(children);
            const hasNonStringChild = childArray.some(
              (child) => typeof child !== "string",
            );

            if (hasNonStringChild) {
              return children;
            }

            const merged = childArray.join("");
            if (!hasMarkdownSyntax(merged)) {
              return children;
            }

            return (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={nestedComponents}
              >
                {merged}
              </ReactMarkdown>
            );
          })()}
        </li>
      ),

      // 引用块样式
      blockquote: ({ children, ...props }) => (
        <blockquote
          className="my-6 pl-4 py-2 border-l-4 border-border_white_transparent20 bg-background_solid_secondary rounded-r-lg"
          {...(props as any)}
        >
          <div className="body_l text-content_tertiary [&_*]:!text-content_tertiary [&_*]:text-inherit">
            {children}
          </div>
        </blockquote>
      ),

      // 代码样式
      code: ({ inline, children, ...props }: any) => (
        <code
          className={cn(
            "font-mono",
            inline
              ? "bg-background_solid_tertiary text-content_brand_primary px-1.5 py-0.5 rounded text-sm"
              : "block bg-background_solid_secondary text-content_primary p-4 rounded-lg text-sm leading-6 overflow-x-auto",
          )}
          {...props}
        >
          {children}
        </code>
      ),

      pre: ({ children, ...props }) => (
        <div className="my-6">
          <pre
            className="bg-background_solid_secondary border border-border_primary rounded-lg p-4 overflow-x-auto"
            {...(props as any)}
          >
            {children}
          </pre>
        </div>
      ),

      // 表格样式
      table: ({ node, ...props }) => (
        <div className="markdown-table relative w-full my-6 overflow-hidden border border-background_white_overlay10 body_medium_m !font-normal [&_table_.quotes-container]:!hidden">
          <div
            className={cn(
              "overflow-x-auto w-full no-scrollbar",
              // isExtension && "overflow-x-hidden",
            )}
          >
            <table
              className={cn(
                "min-w-full table-auto border-separate border-spacing-0 [&_tr>td:first-child]:sticky [&_tr>td:first-child]:left-0 [&_tr>td:first-child]:z-[1] [&_tr>td:first-child]:bg-background_solid_secondary [&_tr>td:first-child]:text-content_primary [&_tr>th:first-child]:sticky [&_tr>th:first-child]:left-0 [&_tr>th:first-child]:z-[1] [&_tr>th:first-child]:bg-background_solid_tertiary",
                // {
                //   "[&_tr>td]:group-hover/tableStyle:bg-background_solid_tertiary [&_tr>th:first-child]:group-hover/tableStyle:bg-background_solid_tertiary":
                //     showSelect,
                // },
              )}
              {...props}
            />
          </div>
        </div>
      ),
      tbody: ({ node, ...props }) => (
        <tbody className="font-normal" {...props} />
      ),
      thead: ({ node, ...props }) => (
        <thead className="font-normal" {...props} />
      ),
      th: ({ node, children, ...props }) => (
        <th
          className="body_medium_s text-left bg-background_solid_tertiary border-0 border-b border-background_white_overlay10 py-2 px-3 text-content_tertiary font-normal overflow-hidden text-ellipsis min-w-[120px]"
          {...props}
        >
          <div
            className={cn(
              "line-clamp-2 overflow-hidden text-ellipsis max-h-[74px]",
              // isExtension && "max-w-full",
            )}
          >
            {children}
          </div>
        </th>
      ),
      tr: ({ node, ...props }) => (
        <tr
          className="text-left bg-transparent text-content_primary py-2 px-3"
          {...props}
        />
      ),
      td: ({ node, children, ...props }) => {
        // Only re-parse string content; React element children are already parsed AST nodes.
        const shouldReparse =
          typeof children === "string" && hasMarkdownSyntax(children);

        const cellContent = shouldReparse ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={nestedComponents}
          >
            {children}
          </ReactMarkdown>
        ) : (
          children
        );

        return (
          <td
            className="border-0 border-b border-border_solid py-2 px-3 min-w-[120px]"
            {...props}
          >
            <div className="body_medium_l">{cellContent}</div>
          </td>
        );
      },

      // 图片样式 - SEO 优化
      img: ({ src, alt, title, ...props }: any) => {
        // 如果没有 alt 文本，尝试从 title 或 src 中生成
        const altText =
          alt ||
          title ||
          (src ? src.split("/").pop()?.split(".")[0] || "" : "");

        if (!src) {
          return null;
        }

        return (
          <Image
            src={src}
            alt={altText}
            title={title || alt || ""}
            width={800}
            height={400}
            className="max-w-full h-auto rounded-lg shadow-sm my-6 mx-auto block"
            loading="lazy"
            {...props}
          />
        );
      },
    };

    const nestedComponents: Partial<Components> = {
      ...baseComponents,
      // Prevent recursive li -> ReactMarkdown -> li loops.
      li: ({ children: nestedChildren, ...nestedProps }) => (
        <li
          className="body_output text-content_primary leading-6"
          {...(nestedProps as any)}
        >
          {nestedChildren}
        </li>
      ),
      // Prevent recursive td -> ReactMarkdown -> td loops.
      td: ({ node, children: tdChildren, ...tdProps }) => (
        <td
          className="border-0 border-b border-border_solid py-2 px-3"
          {...tdProps}
        >
          {tdChildren}
        </td>
      ),
      // Avoid adding extra paragraph spacing in nested rendering.
      p: ({ children: nestedChildren }) => <>{nestedChildren}</>,
    };

    return baseComponents;
  }, []);

  return (
    <article
      aria-label="Docs Markdown article"
      data-openreplay-label="misc_docsmarkdown_article"
      id="sem-docs-markdown-article"
      className={cn("prose prose-lg max-w-none", className)}
      role="main"
    >
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {processedContent}
      </ReactMarkdown>
    </article>
  );
};
